/**
 * Every word the web Squares surfaces show (ISSUE-1269 to ISSUE-1274,
 * ISSUE-1280). Plain, warm American English for a fan on a phone. No money,
 * payment, paid or unpaid, or gambling word may ever appear in Squares copy.
 */
import type {
  typeSquaresGridState,
  typeSquaresGridType,
  typeSquaresPublishStatus,
} from "./squaresTypes";

// Create-pool setup step (screens 01, 01b, 01c, 03, 03b, 05).
export const SQUARES_SETUP_STEP_TITLE = "Squares setup";
export const SQUARES_SETUP_GRID_TYPE_HEADING = "How do you want to play?";
export const SQUARES_SETUP_GAMES_HEADING = "Pick the games";
export const SQUARES_SETUP_AXES_HEADING = "Axes";
export const SQUARES_SETUP_AXES_HINT =
  "What the numbers across the top and down the side stand for.";
export const SQUARES_SETUP_CHECKPOINTS_HEADING = "Winning squares";
export const SQUARES_SETUP_CHECKPOINTS_HINT =
  "When a winning square is set. Final is always on.";
export const SQUARES_SETUP_GRID_SETTINGS_HEADING = "Grid settings";
export const SQUARES_SETUP_NEW_GRID_HEADING = "When a new grid is created";
export const SQUARES_SETUP_LOADING = "Loading the options for this league";
export const SQUARES_SETUP_NO_LEAGUE =
  "Pick a league first. Squares setup depends on it.";
export const SQUARES_SETUP_LEAGUE_UNSUPPORTED =
  "Squares is not ready for this league yet. Go back and pick another one.";

export type typeSquaresGridTypeCopy = {
  title: string;
  body: string;
  points: readonly [string, string, string, string];
  chooseWhen: string;
  chooseOtherWhen: string;
};

export const SQUARES_GRID_TYPE_COPY: Readonly<
  Record<typeSquaresGridType, typeSquaresGridTypeCopy>
> = {
  classic: {
    title: "Classic grid",
    body: "The board you know. Each game gets its own grid with that game's two teams on the axes. One game or many.",
    points: [
      "Every game gets its own grid.",
      "The two teams playing go on the axes, away across the top and home down the side.",
      "Numbers are drawn when the grid locks, so nobody knows them while picking.",
      "The last digit of each team's score picks the winning square.",
    ],
    chooseWhen:
      "One big game, a holiday slate, a weekly night game or one team's season.",
    chooseOtherWhen:
      "You want one grid that carries through a tournament or a playoff run.",
  },
  shared: {
    title: "Shared grid",
    body: "One grid, scored again for every game. Winner and Loser on the axes, not team names. Numbers drawn once.",
    points: [
      "One grid covers a whole run of games.",
      "The axes read Winner and Loser instead of team names.",
      "Numbers are drawn once, at the first lock, and stay put.",
      "Every game in the run picks its own winning squares on the same grid.",
    ],
    chooseWhen:
      "March Madness, the NBA or NHL playoffs, the World Series or any series.",
    chooseOtherWhen:
      "Each game should have its own grid with its own two teams.",
  },
};

export const SQUARES_GRID_TYPE_INFO_LABEL = (title: string): string =>
  `How ${title} works`;
export const SQUARES_GRID_TYPE_CHOOSE_WHEN = "Choose this when";
export const SQUARES_GRID_TYPE_CHOOSE_OTHER = "Choose the other instead when";
export const SQUARES_GRID_TYPE_INFO_CLOSE = "Got it";

export function squaresGridTypeStrip(input: {
  gridType: typeSquaresGridType;
  axesLabel: string | null;
}): string {
  const title = SQUARES_GRID_TYPE_COPY[input.gridType].title;
  return input.axesLabel === null ? title : `${title} · ${input.axesLabel}`;
}

export const SQUARES_ONE_GAME_TODAY = "Today";
export const SQUARES_ONE_GAME_THIS_WEEK = "This week";
export const SQUARES_ONE_GAME_PICK_DATE = "Pick a date";
export const SQUARES_ONE_GAME_LIST_LABEL = "Pick one game";
export const SQUARES_ONE_GAME_EMPTY =
  "No games on these days. Try another day.";
export const SQUARES_CUSTOM_DATES_FROM = "From";
export const SQUARES_CUSTOM_DATES_TO = "To";
export const SQUARES_TEAM_FILTER_LABEL = "Only one team's games";
export const SQUARES_TEAM_FILTER_ALL = "All teams";
export const SQUARES_TEAM_REQUIRED_LABEL = "Team";
/** ISSUE-1275. The calendar under Pick a date: dots mark the days with games. */
export const SQUARES_GAME_DAYS_LOADING = "Loading game days";
export const SQUARES_GAME_DAYS_HINT = "Days with a dot have games.";
/** ISSUE-1266. The Conference preset's picker. */
export const SQUARES_CONFERENCE_LABEL = "Conference";
export const SQUARES_CONFERENCE_PLACEHOLDER = "Pick a conference";
export const SQUARES_CONFERENCE_LOADING = "Loading conferences";
export const SQUARES_CONFERENCE_NONE =
  "We don't have this league's conferences yet. Try another way to pick games.";
export const SQUARES_PREVIEW_LOADING = "Finding the games";
export const SQUARES_PREVIEW_INCLUDE_ALL = "Include all";
export const SQUARES_PREVIEW_STARTED = "Started";
export const SQUARES_PREVIEW_TBD = "TBD";
export const SQUARES_PREVIEW_NOT_SCHEDULED =
  "These games are not on the schedule yet. Your pool is set, and grids show up as the league adds the games.";
export const SQUARES_PREVIEW_ZERO_BLOCKED =
  "No games match yet. Pick a game or dates that have games.";

export function squaresPreviewCountLine(input: {
  matchCount: number;
  includedCount: number;
}): string {
  const games = input.matchCount === 1 ? "game matches" : "games match";
  return `${String(input.matchCount)} ${games} · ${String(input.includedCount)} included`;
}

export function squaresPreviewExpectedMore(count: number): string {
  return count === 1
    ? "1 more game is expected once the league sets it."
    : `${String(count)} more games are expected once the league sets them.`;
}

export function squaresCreateGridCountLine(input: {
  gridType: typeSquaresGridType;
  includedCount: number;
}): string {
  if (input.gridType === "shared") {
    return input.includedCount === 1
      ? "Creates 1 grid for 1 game"
      : `Creates 1 grid for ${String(input.includedCount)} games`;
  }
  return input.includedCount === 1
    ? "Creates 1 grid"
    : `Creates ${String(input.includedCount)} grids`;
}

/**
 * The create footer's button for a Squares pool (ISSUE-1269, owner pick
 * 2026-11-03): it names what tapping makes. "Create 14 grids" when a Classic
 * season starts with more than one grid, "Create grid" for one (a single
 * Classic game, or any Shared grid), and "Create pool" while no grid is made
 * yet (the games are still to come).
 */
export function squaresCreateSubmitLabel(gridCount: number): string {
  if (gridCount <= 0) return "Create pool";
  return gridCount === 1 ? "Create grid" : `Create ${String(gridCount)} grids`;
}

/**
 * The more-than-100 confirmation (ISSUE-1269). Classic makes one grid per
 * game, so it asks about grids; Shared makes one grid scored for every game,
 * so it asks about games.
 */
export function squaresLargeCountConfirmLabel(input: {
  gridType: typeSquaresGridType;
  count: number;
}): string {
  return input.gridType === "shared"
    ? `Yes, score this grid for all ${String(input.count)} games`
    : `Yes, create all ${String(input.count)} grids`;
}

export function squaresLargeCountHint(gridType: typeSquaresGridType): string {
  return gridType === "shared"
    ? "That is a lot of games for one grid. Check the box to keep all of them."
    : "That is a lot of grids. Check the box to keep all of them.";
}

/** The create footer's missing item until the large count gets its yes. */
export function squaresLargeCountMissingItem(input: {
  gridType: typeSquaresGridType;
  count: number;
}): string {
  return input.gridType === "shared"
    ? `a yes to scoring one grid for ${String(input.count)} games`
    : `a yes to creating ${String(input.count)} grids`;
}

// The Review step's Squares summary (ISSUE-1269).
export const SQUARES_REVIEW_TITLE = "Squares setup";
export const SQUARES_REVIEW_GRID_TYPE = "Grid type";
export const SQUARES_REVIEW_GAMES = "Games";
export const SQUARES_REVIEW_AXES = "Axes";
export const SQUARES_REVIEW_CHECKPOINTS = "Winning squares";
export const SQUARES_REVIEW_SIZE = "Squares on grid";
export const SQUARES_REVIEW_MAX_PER_MEMBER = "Max squares per member";
export const SQUARES_REVIEW_AUTO_LOCK = "Auto-lock";
export const SQUARES_REVIEW_SHOW_NUMBERS = "Show numbers to members";
export const SQUARES_REVIEW_ON = "On";
export const SQUARES_REVIEW_OFF = "Off";
export function squaresGridTypeName(gridType: typeSquaresGridType): string {
  return SQUARES_GRID_TYPE_COPY[gridType].title;
}

export function squaresFirstLockLine(lockLabel: string): string {
  return `First lock: ${lockLabel}`;
}

export const SQUARES_AXES_CUSTOM_TOP = "Across the top";
export const SQUARES_AXES_CUSTOM_SIDE = "Down the side";
export const SQUARES_AXES_CUSTOM_HINT =
  "With your own labels, every winner is added by hand.";
export const SQUARES_AXES_TEAM_PICKED = (teamName: string): string =>
  `${teamName} across the top, Opponent down the side`;

export const SQUARES_GRID_SIZE_LABEL = "Squares on grid";
export const SQUARES_GRID_SIZE_OPTION = (size: number): string => {
  if (size === 25) return "25 (5 by 5)";
  if (size === 50) return "50 (5 by 10)";
  return "100 (10 by 10)";
};
export const SQUARES_GRID_SIZE_HINT_SMALL =
  "Each row and column covers two numbers.";
export const SQUARES_MAX_PER_MEMBER_LABEL = "Max squares per member";
export const SQUARES_MAX_PER_MEMBER_HINT =
  "Per grid. Leave it empty for unlimited.";
export const SQUARES_MAX_PER_MEMBER_UNLIMITED = "Unlimited";
export const SQUARES_MAX_PER_MEMBER_ERROR = "Enter a number of 1 or more.";
/** What a game's start is called in each sport (ISSUE-1269). */
function squaresGameStartWord(leagueKey: string | null | undefined): string {
  const sport = (leagueKey ?? "").split("-")[0];
  if (sport === "basketball") return "tip-off";
  if (sport === "baseball") return "first pitch";
  if (sport === "hockey") return "puck drop";
  return "kickoff";
}

/**
 * The auto-lock switch. Classic locks each grid at its own game's start in
 * the sport's own word ("at kickoff", "at tip-off"); Shared locks once, when
 * the run's first game starts.
 */
export function squaresAutoLockLabel(input: {
  gridType: typeSquaresGridType | null;
  leagueKey: string | null | undefined;
}): string {
  return input.gridType === "shared"
    ? "Auto-lock when the first game starts"
    : `Auto-lock at ${squaresGameStartWord(input.leagueKey)}`;
}
/**
 * The line under the auto-lock switch, in the same sport word as its label
 * (QA round 3): Classic draws at each game's own start, Shared at the run's
 * first game.
 */
export function squaresAutoLockDescription(input: {
  gridType: typeSquaresGridType | null;
  leagueKey: string | null | undefined;
}): string {
  const start = squaresGameStartWord(input.leagueKey);
  return input.gridType === "shared"
    ? `Squares freeze and numbers are drawn at the first game's ${start}.`
    : `Squares freeze and numbers are drawn at ${start}.`;
}
export const SQUARES_SHOW_NUMBERS_LABEL = "Show numbers to members";
export const SQUARES_SHOW_NUMBERS_DESCRIPTION =
  "Off keeps the numbers hidden from members. Admins always see them.";
export const SQUARES_CARRY_SQUARES_LABEL = "Carry squares forward";
export const SQUARES_CARRY_SQUARES_DESCRIPTION =
  "Everyone keeps the same squares on the next grid.";
export const SQUARES_REDRAW_NUMBERS_LABEL = "Redraw numbers every grid";
export const SQUARES_REDRAW_NUMBERS_DESCRIPTION =
  "Off keeps the first grid's numbers for every grid.";
export const SQUARES_AUTO_PUBLISH_LABEL = "Auto-publish new grids";
export const SQUARES_AUTO_PUBLISH_DESCRIPTION =
  "Off saves new grids as drafts only admins can see.";

// Grid status (Game tab, grid list, pool card).
export function squaresGridStatusLabel(input: {
  gridState: typeSquaresGridState;
  publishStatus: typeSquaresPublishStatus;
}): string {
  if (input.publishStatus === "draft") return "Draft";
  switch (input.gridState) {
    case "open":
      return "Open";
    case "locked":
      return "Locked";
    case "live":
      return "Live";
    case "final":
      return "Final";
    case "void":
      return "Void";
  }
}

// Game tab (screens 14, 15, 16).
export const SQUARES_GAME_LOADING = "Loading your Squares board";
export const SQUARES_GAME_ERROR =
  "We could not load the board. Pull down or try again in a moment.";
export const SQUARES_GAME_TRY_AGAIN = "Try again";
export const SQUARES_EMPTY_NO_SEASON =
  "This pool has no Squares season right now.";
export function squaresEmptyAwaitingSchedule(leagueName: string): string {
  return `Grids appear when the ${leagueName} publishes the schedule for this preset. Members are in; nothing to do yet.`;
}
export const SQUARES_PICK_GRID_LABEL = "Pick a grid";
export const SQUARES_GRID_LIST_TITLE = "Grids";
export const SQUARES_GRID_LIST_DRAFT_NOTE = "Only admins see drafts.";
export function squaresGridOfLine(input: {
  sequence: number;
  total: number;
}): string {
  return `grid ${String(input.sequence)} of ${String(input.total)}`;
}
export function squaresGameOfLine(input: {
  index: number;
  total: number;
}): string {
  return `game ${String(input.index)} of ${String(input.total)}`;
}
export const SQUARES_MANAGE_GRID = "Manage Grid";
export function squaresMySquaresLine(input: {
  count: number;
  max: number | null;
}): string {
  return input.max === null
    ? `My squares ${String(input.count)}`
    : `My squares ${String(input.count)} of ${String(input.max)}`;
}
export const SQUARES_TAKE_BLOCKED: Readonly<
  Record<"gridClosed" | "limitReached" | "notActiveMember", string>
> = {
  gridClosed: "This grid is not open for picking.",
  limitReached: "You have all the squares you can hold on this grid.",
  notActiveMember: "Join the pool to pick squares.",
};
export const SQUARES_BOARD_LABEL = "Squares board";
export const SQUARES_UNKNOWN_DIGIT = "?";
export function squaresSquareAriaLabel(input: {
  row: number;
  col: number;
  holder: string | null;
  isMine: boolean;
  isWinning: boolean;
}): string {
  const place = `Row ${String(input.row + 1)}, column ${String(input.col + 1)}`;
  const holder =
    input.holder === null
      ? "open"
      : input.isMine
        ? "your square"
        : `held by ${input.holder}`;
  return input.isWinning
    ? `${place}, ${holder}, winning square`
    : `${place}, ${holder}`;
}
export const SQUARES_TAKEN_REFETCH =
  "Someone just took that square. The board is up to date now.";

// Winning squares card.
export const SQUARES_WINNING_TITLE = "Winning squares";
export const SQUARES_WINNING_UPCOMING = "Set automatically when the game ends";
const SQUARES_WINNING_UPCOMING_BY_CHECKPOINT: Readonly<Record<string, string>> =
  {
    q1: "Set automatically after the 1st quarter",
    half: "Set automatically at halftime",
    q3: "Set automatically after the 3rd quarter",
    p1: "Set automatically after the 1st period",
    p2: "Set automatically after the 2nd period",
    after3: "Set automatically after the 3rd inning",
    after5: "Set automatically after the 5th inning",
    after7: "Set automatically after the 7th inning",
  };
/** When a checkpoint not reached yet gets its winner. Final reads "when the game ends". */
export function squaresWinningUpcomingLine(checkpointKey: string): string {
  return (
    SQUARES_WINNING_UPCOMING_BY_CHECKPOINT[checkpointKey] ??
    SQUARES_WINNING_UPCOMING
  );
}
export const SQUARES_WINNING_WAITING_FINAL =
  "Set at the final, once the winner is known";
/**
 * A checkpoint the scoring job never sets (NCAAB Half, or any checkpoint on a
 * Custom axes grid). The server says so with the "adminSets" row state, so
 * the row never promises an automatic winner (ISSUE-1270 QA round 3).
 */
export const SQUARES_WINNING_ADMIN_SETS = "An admin sets this winner";
/** The line a checkpoint row without a winner shows, by its server state. */
export function squaresWinningPendingLine(row: {
  checkpointKey: string;
  state: string;
}): string {
  if (row.state === "waitingForFinal") return SQUARES_WINNING_WAITING_FINAL;
  if (row.state === "adminSets") return SQUARES_WINNING_ADMIN_SETS;
  return squaresWinningUpcomingLine(row.checkpointKey);
}
export const SQUARES_WINNING_UNCLAIMED = "Unclaimed square";
export const SQUARES_WINNING_SET_BY_ADMIN = "Set by admin";
export const SQUARES_WINNING_ADDED_BY_ADMIN = "Added by admin";
export const SQUARES_ADD_WINNER = "+ Add a winner";
export function squaresAllWinnersLine(count: number): string {
  return count === 1
    ? "1 winner on Results"
    : `All ${String(count)} winners on Results`;
}
export const SQUARES_GUEST_TAG = "Guest";

// Assign square sheet (screens 07, 07b).
export const SQUARES_ASSIGN_TITLE = "Assign square";
export function squaresAssignPlace(input: {
  row: number;
  col: number;
  gameLabel: string;
}): string {
  return `Row ${String(input.row + 1)}, column ${String(input.col + 1)} · ${input.gameLabel}`;
}
export const SQUARES_ASSIGN_CURRENT = "Holds this square";
export const SQUARES_ASSIGN_RELEASE = "Release square";
export const SQUARES_ASSIGN_SEARCH = "Search members and guests";
export const SQUARES_ASSIGN_GUESTS_HEADING = "Guests in this pool";
export const SQUARES_ASSIGN_MEMBERS_HEADING = "Members";
export const SQUARES_ASSIGN_NONE_FOUND = "Nobody matches that name.";
export const SQUARES_ASSIGN_LIST_LABEL = "Who gets this square";
export function squaresAssignCountLine(input: {
  count: number;
  max: number | null;
  isAtLimit: boolean;
}): string {
  const base =
    input.max === null
      ? `${String(input.count)} squares`
      : `${String(input.count)} of ${String(input.max)} squares`;
  return input.isAtLimit ? `${base} · at limit` : base;
}
export const SQUARES_ASSIGN_NOT_JOINED = "hasn't joined this grid";
export function squaresAssignTo(name: string): string {
  return `Assign to ${name}`;
}
export const SQUARES_ASSIGN_PICK_SOMEONE = "Assign square";
export const SQUARES_ADD_GUEST = "+ Add a guest";
export const SQUARES_GUEST_FIRST_NAME = "First name";
export const SQUARES_GUEST_LAST_NAME = "Last name";
export const SQUARES_GUEST_INITIALS = "Initials";
export const SQUARES_GUEST_INITIALS_HINT =
  "Up to 2 letters. Shown on the squares.";
export const SQUARES_GUEST_SUBMIT = "Add guest and assign";
export const SQUARES_GUEST_BACK = "Back to the list";
export const SQUARES_GUEST_NOTE =
  "Guests are saved to this pool, so you can give them squares on any grid. They do not get notifications.";
export const SQUARES_GUEST_NAME_REQUIRED = "Enter a first name.";

// Manage Grid sheet (screen 08).
export const SQUARES_MANAGE_TITLE = "Manage Grid";
export const SQUARES_MANAGE_PUBLISH = "Publish grid";
export const SQUARES_MANAGE_PUBLISH_HINT =
  "This grid is a draft. Only admins can see it.";
export const SQUARES_MANAGE_NOTIFY = "Notify members";
export const SQUARES_MANAGE_UNPUBLISH = "Move back to draft";
export const SQUARES_MANAGE_LOCK = "Lock squares";
export function squaresManageLockHint(lockLabel: string | null): string {
  return lockLabel === null
    ? "Freezes the squares and draws the numbers."
    : `Locks on its own at ${lockLabel}. Freezes the squares and draws the numbers.`;
}
export const SQUARES_MANAGE_UNLOCK = "Unlock squares";
export const SQUARES_MANAGE_UNLOCK_HINT =
  "Opens the grid again until the game starts.";
export const SQUARES_MANAGE_ASSIGN = "Assign square to member";
export const SQUARES_MANAGE_ASSIGN_HINT =
  "Tap any square on the board to give it to someone.";
export const SQUARES_MANAGE_COPY = "Copy squares from another grid";
export const SQUARES_MANAGE_COPY_SOURCE = "Copy from";
export const SQUARES_MANAGE_COPY_MERGE = "Add to the squares here";
export const SQUARES_MANAGE_COPY_REPLACE = "Replace the squares here";
export const SQUARES_MANAGE_COPY_MODE = "How to copy";
export const SQUARES_MANAGE_COPY_SUBMIT = "Copy squares";
export function squaresCopyResult(input: {
  copiedCount: number;
  skippedCount: number;
}): string {
  const copied =
    input.copiedCount === 1
      ? "Copied 1 square."
      : `Copied ${String(input.copiedCount)} squares.`;
  if (input.skippedCount === 0) return copied;
  return `${copied} Skipped ${String(input.skippedCount)} already taken.`;
}
export const SQUARES_MANAGE_RANDOMIZE = "Randomize numbers";
export const SQUARES_MANAGE_RANDOMIZE_HINT =
  "Draws new numbers. Not available once the game starts or a winner is set.";
export const SQUARES_MANAGE_UNPUBLISH_BLOCKED =
  "A winner is set on this grid, so it stays published.";
/** The Remove button's name for one winner row (ISSUE-1272). */
export function squaresRemoveWinnerName(input: {
  label: string;
  holder: string;
}): string {
  return `Remove ${input.label} winner, ${input.holder}`;
}
export const SQUARES_MANAGE_WINNERS = "Winners";
export const SQUARES_MANAGE_NO_WINNERS = "No winners on this grid yet.";
export const SQUARES_MANAGE_REMOVE_WINNER = "Remove";
export const SQUARES_MANAGE_CLEAR = "Clear selections";
export const SQUARES_MANAGE_CLEAR_HINT =
  "Empties every square. Not available after the grid locks.";
export const SQUARES_MANAGE_CLOSE = "Done";
export const SQUARES_CLEAR_CONFIRM_TITLE = "Clear every square on this grid?";
export const SQUARES_CLEAR_CONFIRM_BODY =
  "Every member and guest loses their squares on this grid. They can pick again while it is open.";
export const SQUARES_CLEAR_CONFIRM_LABEL = "Clear squares";
export const SQUARES_REMOVE_WINNER_CONFIRM_TITLE = "Remove this winner?";
export const SQUARES_REMOVE_WINNER_CONFIRM_BODY =
  "The winner comes off the grid and Results. It will not be set again automatically.";
export const SQUARES_REMOVE_WINNER_CONFIRM_LABEL = "Remove winner";
export const SQUARES_RELEASE_CONFIRM_TITLE = "Release this square?";
export const SQUARES_RELEASE_CONFIRM_BODY =
  "The square goes back to open, and anyone can pick it.";
export const SQUARES_RELEASE_CONFIRM_LABEL = "Release square";

// Add a winner sheet (screen 10).
export const SQUARES_ADD_WINNER_TITLE = "Add a winner";
export const SQUARES_ADD_WINNER_BY_NUMBERS = "Enter both numbers";
export const SQUARES_ADD_WINNER_ON_GRID = "Pick on grid";
export const SQUARES_ADD_WINNER_HOW = "How to pick the square";
export const SQUARES_ADD_WINNER_GAME = "Game";
export function squaresWinnerDigitFieldLabel(axisLabel: string): string {
  return `${axisLabel} number`;
}
export const SQUARES_ADD_WINNER_DIGIT_ERROR = "Enter one number from 0 to 9.";
export const SQUARES_ADD_WINNER_LABEL_HEADING = "Label";
export const SQUARES_ADD_WINNER_CUSTOM = "Custom";
export const SQUARES_ADD_WINNER_CUSTOM_LABEL = "Custom label";
export const SQUARES_ADD_WINNER_CUSTOM_PLACEHOLDER = "Reverse final";
export const SQUARES_ADD_WINNER_REPLACES =
  "A checkpoint label replaces the automatic winner for that checkpoint on this grid.";
export const SQUARES_ADD_WINNER_ADDS =
  "A custom label adds one more winner. Members see it marked as added by an admin.";
export const SQUARES_ADD_WINNER_SUBMIT = "Add winner";
export const SQUARES_ADD_WINNER_HOLDER = "Holder";
export const SQUARES_ADD_WINNER_PICK_PROMPT = "Tap the winning square.";

// Results tab (screens 17, 18).
export const SQUARES_RESULTS_VIEW_LABEL = "Results view";
export const SQUARES_RESULTS_STANDINGS = "Standings";
export const SQUARES_RESULTS_WINNERS = "Winners";
export const SQUARES_RESULTS_LOADING = "Loading results";
export const SQUARES_RESULTS_ALL_USERS = "All Users";
export const SQUARES_RESULTS_ALL_GAMES = "All Games";
export const SQUARES_RESULTS_USER_FILTER = "Member";
export const SQUARES_RESULTS_GAME_FILTER = "Game";
export const SQUARES_RESULTS_CHIP_ALL = "All";
export const SQUARES_RESULTS_CHIPS_LABEL = "Checkpoint";
export const SQUARES_RESULTS_EMPTY_STANDINGS =
  "No winning squares yet. Standings fill in as games are scored.";
export const SQUARES_RESULTS_EMPTY_WINNERS =
  "No winners match. Winning squares show up here as games are scored.";
export const SQUARES_RESULTS_ADD_WINNER = "+ Add a winner to this grid";
export function squaresStandingSummary(input: {
  breakdown: readonly { label: string; count: number }[];
  squareCount: number;
}): string {
  const parts = input.breakdown
    .filter((item) => item.count > 0)
    .map((item) => `${item.label} ×${String(item.count)}`);
  const squares =
    input.squareCount === 1
      ? "1 square"
      : `${String(input.squareCount)} squares`;
  return [...parts, squares].join(" · ");
}
export function squaresWinCountLabel(count: number): string {
  return count === 1 ? "1 winning square" : `${String(count)} winning squares`;
}

// Settings tab (screens 12, 13).
export const SQUARES_SETTINGS_POOL_CARD = "Pool";
export const SQUARES_SETTINGS_SET_AT_CREATION = "Set at creation";
export const SQUARES_SETTINGS_LEAGUE = "League";
export const SQUARES_SETTINGS_GRID_TYPE = "Grid type";
export const SQUARES_SETTINGS_GAMES = "Games";
export const SQUARES_SETTINGS_SEASON = "Season";
export const SQUARES_SETTINGS_ADD_GRID = "+ Add a grid";
export const SQUARES_SETTINGS_GRID_RULES = "Grid rules";
export const SQUARES_SETTINGS_LOCKED_AFTER_FIRST_LOCK =
  "Locked after first lock";
export const SQUARES_SETTINGS_AXES = "Axes";
export const SQUARES_SETTINGS_CHECKPOINTS = "Winning squares";
export const SQUARES_SETTINGS_SAVED = "Saved";
export const SQUARES_SETTINGS_NEW_SEASON = "New season";
export const SQUARES_SETTINGS_NEW_SEASON_READY =
  "Every grid is final. Start the next season whenever you are ready.";
export const SQUARES_SETTINGS_NEW_SEASON_WAITING =
  "Available once every grid is final.";
export const SQUARES_SETTINGS_START_NEW_SEASON = "Start new season";
export const SQUARES_SETTINGS_RULES_HEADING = "How this pool works";
export const SQUARES_SETTINGS_LOADING = "Loading Squares settings";
export function squaresSeasonLine(seasonNumber: number): string {
  return `Season ${String(seasonNumber)}`;
}
export function squaresGamesSummary(input: {
  gamesLabel: string;
  gameCount: number;
  windowLabel: string | null;
}): string {
  const count =
    input.gameCount === 1 ? "1 game" : `${String(input.gameCount)} games`;
  return [input.gamesLabel, count, input.windowLabel]
    .filter((part): part is string => part !== null && part !== "")
    .join(" · ");
}

// Add a grid sheet (screen 09).
export const SQUARES_ADD_GRID_TITLE = "Add a grid";
export const SQUARES_ADD_GRID_DESCRIPTION =
  "Same league, same members and same settings, in this season.";
export const SQUARES_ADD_GRID_THIS_GRID = "This grid";
export const SQUARES_ADD_GRID_MEMBERS = "Members";
export const SQUARES_ADD_GRID_MEMBERS_VALUE = "Everyone in the pool";
export const SQUARES_ADD_GRID_SQUARES = "Squares";
export const SQUARES_ADD_GRID_SQUARES_CARRY =
  "Carried forward from the last grid";
export const SQUARES_ADD_GRID_SQUARES_EMPTY = "Start empty";
export const SQUARES_ADD_GRID_NUMBERS = "Numbers";
export const SQUARES_ADD_GRID_NUMBERS_REDRAW = "Drawn fresh at lock";
export const SQUARES_ADD_GRID_NUMBERS_KEEP = "Same as the first grid";
export const SQUARES_ADD_GRID_PUBLISH_NOW = "Publish now";
export const SQUARES_ADD_GRID_PUBLISH_NOW_DESCRIPTION =
  "Off saves it as a draft only admins can see.";
export const SQUARES_ADD_GRID_NOTIFY = "Notify members";
export const SQUARES_ADD_GRID_SUBMIT = "Add grid";
export const SQUARES_ADD_GRID_CANCEL = "Cancel";

// Start a new season sheet (screen 11).
export const SQUARES_NEW_SEASON_TITLE = "Start a new season";
export const SQUARES_NEW_SEASON_NEXT_GAMES = "Next games";
export const SQUARES_NEW_SEASON_CARRIES = "What carries over";
export const SQUARES_NEW_SEASON_MEMBERS = "Members";
export function squaresNewSeasonMembersValue(count: number): string {
  return count === 1 ? "1 member" : `All ${String(count)} members`;
}
export const SQUARES_NEW_SEASON_SETTINGS = "Settings";
export const SQUARES_NEW_SEASON_SETTINGS_VALUE = "Kept as they are";
export const SQUARES_NEW_SEASON_NUMBERS = "Numbers";
export const SQUARES_NEW_SEASON_NUMBERS_VALUE = "Always drawn fresh";
export const SQUARES_NEW_SEASON_CARRY_SQUARES = "Keep everyone's squares";
export const SQUARES_NEW_SEASON_CARRY_SQUARES_DESCRIPTION =
  "Off starts every grid empty.";
export const SQUARES_NEW_SEASON_ARCHIVED = "What is archived";
export function squaresNewSeasonArchivedValue(input: {
  gridCount: number;
  winnerCount: number;
}): string {
  const grids =
    input.gridCount === 1 ? "1 grid" : `${String(input.gridCount)} grids`;
  const winners =
    input.winnerCount === 1
      ? "1 winner"
      : `${String(input.winnerCount)} winners`;
  return `${grids}, ${winners} and this season's standings. Draft grids are deleted.`;
}
export const SQUARES_NEW_SEASON_BLOCKED =
  "Every published grid has to be final before a new season starts.";
export const SQUARES_NEW_SEASON_NO_SEASON =
  "This pool has no season to follow.";
export const SQUARES_NEW_SEASON_NEEDS_GAME =
  "Pick the game for the new season.";
export const SQUARES_NEW_SEASON_NEEDS_DATES =
  "Pick the dates for the new season.";
export const SQUARES_NEW_SEASON_SUBMIT = "Start season";

// Custom games with no feed (ISSUE-1282), after v1's custom_pool_match_form.dart.
export const SQUARES_ADD_GRID_SOURCE_LABEL = "Game";
export const SQUARES_ADD_GRID_SOURCE_SCHEDULE = "On the schedule";
export const SQUARES_ADD_GRID_SOURCE_CUSTOM = "Custom game";
export const SQUARES_CUSTOM_GRID_DESCRIPTION =
  "A game we don't follow. Name it and both sides, lock it when you're ready, and add the winners yourself.";
export const SQUARES_CUSTOM_GRID_NAME_LABEL = "Game name";
export const SQUARES_CUSTOM_GRID_NAME_HINT =
  "Leave it blank and we'll call it Custom game with a number.";
export const SQUARES_CUSTOM_GRID_NAME_PLACEHOLDER = "Office Bowl";
export const SQUARES_CUSTOM_GRID_NAME_MAX_LENGTH = 40;
export const SQUARES_CUSTOM_GRID_TOP_LABEL = "Across the top";
export const SQUARES_CUSTOM_GRID_TOP_PLACEHOLDER = "Blue team";
export const SQUARES_CUSTOM_GRID_SIDE_LABEL = "Down the side";
export const SQUARES_CUSTOM_GRID_SIDE_PLACEHOLDER = "Red team";
export const SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH = 20;
export const SQUARES_CUSTOM_GRID_NUMBERS_VALUE = "Drawn when you lock it";
export const SQUARES_CUSTOM_GRID_WINNERS = "Winners";
export const SQUARES_CUSTOM_GRID_WINNERS_VALUE = "Added by an admin";
export const SQUARES_CUSTOM_GRID_RENAME = "Rename game";
export const SQUARES_CUSTOM_GRID_RENAME_TITLE = "Rename game";
export const SQUARES_CUSTOM_GRID_RENAME_SAVE = "Save changes";
/** ISSUE-1282. The Manage Grid section that edits a custom game's name and side names. */
export const SQUARES_CUSTOM_GRID_EDIT = "Edit game";
export const SQUARES_CUSTOM_GRID_TAG = "Custom game";
